/**
 * ProgressMeter — % complete indicator for async Resmo tasks.
 *
 * Self-drives a smooth asymptotic % while mounted (fast start, slows as it
 * approaches 95%, calibrated by expectedSeconds). Mount it while a task is
 * running; unmount on completion. Replaces "Xs elapsed" style counters.
 *
 * Props:
 *   expectedSeconds  number   typical task duration (drives the curve)
 *   label            string   optional text before the %
 *   compact          bool     inline text-only variant (no bar)
 *   testid           string   data-testid
 */
import React, { useEffect, useState } from "react";

export function useTaskProgress(expectedSeconds = 30) {
  const [pct, setPct] = useState(2);
  useEffect(() => {
    const start = Date.now();
    const t = setInterval(() => {
      const s = (Date.now() - start) / 1000;
      const p = Math.round(100 * (1 - Math.exp(-s / (expectedSeconds * 0.55))));
      setPct(Math.max(2, Math.min(95, p)));
    }, 400);
    return () => clearInterval(t);
  }, [expectedSeconds]);
  return pct;
}

export default function ProgressMeter({ expectedSeconds = 30, label = "", compact = false, testid = "progress-meter" }) {
  const pct = useTaskProgress(expectedSeconds);

  if (compact) {
    return (
      <span className="font-mono-rm text-[11px] uppercase tracking-widest"
            style={{ color: "#B45309", fontWeight: 700 }}
            data-testid={testid}>
        {label ? `${label} · ` : "· "}{pct}% complete
      </span>
    );
  }

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        {label ? (
          <span className="font-mono-rm text-[10px] uppercase tracking-widest"
                style={{ color: "#475569", fontWeight: 700 }}>
            · {label}
          </span>
        ) : <span />}
        <span className="font-mono-rm text-[11px] uppercase tracking-widest"
              style={{ color: "#FF6A00", fontWeight: 700 }}
              data-testid={`${testid}-pct`}>
          {pct}% complete
        </span>
      </div>
      <div className="h-2 rounded-full overflow-hidden"
           style={{ background: "#E2E8F0", border: "1px solid #0F1B2D" }}>
        <div className="h-full transition-all duration-500"
             style={{ width: `${pct}%`, background: "linear-gradient(90deg,#FF6A00,#FFC145)" }} />
      </div>
    </div>
  );
}
